/**
 * Bloque 3 del rediseño — autoservicio para token de activación vencido.
 *
 * El residente que abre un link `/activar/:token` vencido (o inválido) es
 * redirigido acá. Ingresa su email y se le manda un nuevo link sin pasar
 * por el admin.
 *
 * Se accede vía:
 *  - Ruta directa: /activar/expirado (App.js)
 *  - Redirect desde ActivarCuenta.jsx cuando detecta token expirado.
 *
 * No requiere auth (residente no logueado).
 *
 * /api/resend-activation responde lo mismo ("sent") haya una invitación
 * pendiente, una cuenta ya activa o nada para ese email, así no deja averiguar
 * qué emails están registrados. Por eso el texto de respuesta es uno solo y no
 * dice si el email existe.
 */
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { colors, radius, spacing, shadows, typography, btnPrimary, btnSecondary } from "../utils/designTokens";
import { HABI_API_BASE_URL } from "../utils/habiApi";

// Textos del reenvío. ActivarCuenta.jsx (link vencido, reenvío de un toque)
// muestra los mismos.
export const MSG_REENVIO_ENVIADO =
  "Si hay una invitación pendiente para ese email, te mandamos un link nuevo. Si ya tenés cuenta, te mandamos un aviso para entrar. Revisá también el correo no deseado.";
export const MSG_REENVIO_NO_LLEGA =
  "¿No te llega? Pedile al administrador de tu edificio que reenvíe la invitación.";

const MSG_EMAIL_INVALIDO = "Ingresá un email válido.";
const MSG_FALLO_ENVIO = "No pudimos procesar el pedido. Revisá tu conexión e intentá de nuevo en unos minutos.";
// 429 del cupo de las rutas públicas de propia_agent (RateLimitMiddleware): no
// es un fallo de conexión, hay que esperar. No dice nada del email.
const MSG_ESPERAR = "Recibimos varios pedidos seguidos desde tu conexión. Probá de nuevo en unos minutos.";

// El mismo formato que valida /api/resend-activation.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "no_account" y "already_active" los devolvía el backend anterior: mientras
// conviva con este front se muestran con el mismo texto que "sent".
const RESPUESTAS_ENVIADO = new Set(["sent", "no_account", "already_active"]);

const ActivacionExpirada = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Si la pantalla anterior nos pasó el email del token vencido, pre-rellenar.
  const initialEmail = location.state?.email || "";

  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    // Sin pasar a minúsculas: el servidor lo normaliza y, con el email tal
    // cual, también encuentra las fichas viejas guardadas con mayúsculas.
    const limpio = email.trim();
    if (!EMAIL_RE.test(limpio)) {
      setError(MSG_EMAIL_INVALIDO);
      return;
    }
    setSubmitting(true);
    try {
      // Canal confiable habi@habitia.ai (renueva el token y manda el link).
      const resp = await fetch(`${HABI_API_BASE_URL}/api/resend-activation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: limpio }),
      });
      const status = resp.ok ? (await resp.json())?.status : undefined;
      if (RESPUESTAS_ENVIADO.has(status)) {
        setSubmitted(true);
      } else if (status === "invalid") {
        setError(MSG_EMAIL_INVALIDO);
      } else if (resp.status === 429) {
        setError(MSG_ESPERAR);
      } else {
        // HTTP con error, "error" del servidor o una respuesta desconocida: no
        // se mandó nada, así que no se le dice que sí; puede reintentar.
        console.warn("[ActivacionExpirada] resend-activation respondió:", resp.status, status);
        setError(MSG_FALLO_ENVIO);
      }
    } catch (err) {
      console.error("[ActivacionExpirada] resend-activation falló:", err);
      setError(MSG_FALLO_ENVIO);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.bgMain,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing[4],
        fontFamily: typography.fontFamily,
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: radius.md,
          padding: spacing[8],
          maxWidth: 480,
          width: "100%",
          boxShadow: shadows.modal,
        }}
      >
        <h1
          style={{
            fontSize: 20,
            color: colors.primary,
            fontWeight: 400,
            margin: 0,
            marginBottom: spacing[3],
          }}
        >
          Tu link expiró
        </h1>

        {submitted ? (
          <>
            <p
              role="status"
              style={{
                fontSize: 14,
                color: colors.gray900,
                lineHeight: 1.6,
                margin: 0,
                marginBottom: spacing[5],
              }}
            >
              {MSG_REENVIO_ENVIADO}
            </p>
            <p
              style={{
                fontSize: 13,
                color: colors.gray600,
                lineHeight: 1.5,
                margin: 0,
                marginBottom: spacing[5],
              }}
            >
              {MSG_REENVIO_NO_LLEGA}
            </p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="habitia-btn-secondary"
              style={{ ...btnSecondary, justifyContent: "center", width: "100%" }}
            >
              Volver al login
            </button>
          </>
        ) : (
          <>
            <p
              style={{
                fontSize: 14,
                color: colors.gray900,
                lineHeight: 1.6,
                margin: 0,
                marginBottom: spacing[2],
              }}
            >
              Los links de activación vencen a las 48 horas por seguridad.
            </p>
            <p
              style={{
                fontSize: 14,
                color: colors.gray900,
                lineHeight: 1.6,
                margin: 0,
                marginBottom: spacing[5],
              }}
            >
              Ingresá tu email y te enviamos uno nuevo:
            </p>

            <form onSubmit={handleSubmit}>
              <label
                htmlFor="activacion-email"
                style={{
                  display: "block",
                  fontSize: 13,
                  color: colors.gray900,
                  marginBottom: spacing[2],
                  fontWeight: 400,
                }}
              >
                Email
              </label>
              <input
                id="activacion-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@dominio.com"
                disabled={submitting}
                className="habitia-input"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${colors.gray200}`,
                  borderRadius: radius.sm,
                  fontSize: 14,
                  fontFamily: typography.fontFamily,
                  marginBottom: error ? spacing[2] : spacing[5],
                  background: colors.bgCard,
                  color: colors.gray900,
                }}
              />
              {error && (
                <p
                  role="alert"
                  style={{
                    color: colors.errorDark,
                    fontSize: 12,
                    lineHeight: 1.5,
                    margin: 0,
                    marginBottom: spacing[5],
                  }}
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="habitia-btn-primary"
                style={{
                  ...btnPrimary,
                  justifyContent: "center",
                  width: "100%",
                  cursor: submitting ? "wait" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? "Enviando..." : "Enviar nuevo link"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivacionExpirada;
